import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import { useReactToPrint } from 'react-to-print'
import CVForm from './CVForm'
import CVPreview from './CVPreview'
import exampleCV from './Utils/exampleCV'
import emptyCV from './Utils/emptyCV'

const Main = () => {
  const [cv, setCv] = useState(emptyCV)

  const handleChangePersonal = (e) => {
    const { name, value, type } = e.target

    if (type === 'file') {
      handleChangeFile(e)
      return
    }

    setCv((prevState) => ({
      ...prevState,
      personalInfo: {
        ...prevState.personalInfo,
        [name]: value,
      },
    }))
  }

  const handleChangeFile = (e) => {
    const { name } = e.target
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      setCv((prevState) => ({
        ...prevState,
        personalInfo: {
          ...prevState.personalInfo,
          [name]: reader.result,
        },
      }))
    }
    reader.readAsDataURL(file)
  }

  const handleChangeExperience = (e, id) => {
    const { name, value } = e.target

    setCv((prevState) => {
      const newExperience = prevState.experience.map((experienceItem) => {
        if (experienceItem.id === id) {
          return { ...experienceItem, [name]: value }
        }
        return experienceItem
      })
      return { ...prevState, experience: [...newExperience] }
    })
  }

  const handleAddExperience = () => {
    setCv((prevState) => ({
      ...prevState,
      experience: [
        ...prevState.experience,
        {
          id: uuidv4(),
          position: '',
          company: '',
          city: '',
          from: '',
          to: '',
        },
      ],
    }))
  }

  const handleDeleteExperience = (id) => {
    setCv((prevState) => {
      const newExperience = prevState.experience.filter(
        (experienceItem) => experienceItem.id !== id
      )
      return { ...prevState, experience: [...newExperience] }
    })
  }

  const handleChangeEducation = (e, id) => {
    const { name, value } = e.target

    setCv((prevState) => {
      const newEducation = prevState.education.map((educationItem) => {
        if (educationItem.id === id) {
          return { ...educationItem, [name]: value }
        }
        return educationItem
      })
      return { ...prevState, education: [...newEducation] }
    })
  }

  const handleAddEducation = () => {
    setCv((prevState) => ({
      ...prevState,
      education: [
        ...prevState.education,
        {
          id: uuidv4(),
          universityName: '',
          city: '',
          degree: '',
          subject: '',
          from: '',
          to: '',
        },
      ],
    }))
  }

  const handleDeleteEducation = (id) => {
    setCv((prevState) => {
      const newEducation = prevState.education.filter(
        (educationItem) => educationItem.id !== id
      )
      return { ...prevState, education: [...newEducation] }
    })
  }

  const handleChangeProject = (e, id) => {
    const { name, value } = e.target

    setCv((prevState) => {
      const newProjects = prevState.projects.map((projectItem) => {
        if (projectItem.id === id) {
          return { ...projectItem, [name]: value }
        }
        return projectItem
      })
      return { ...prevState, projects: [...newProjects] }
    })
  }

  const handleAddProject = () => {
    setCv((prevState) => ({
      ...prevState,
      projects: [
        ...prevState.projects,
        {
          id: uuidv4(),
          projectName: '',
          date: '',
          description: '',
        },
      ],
    }))
  }

  const handleDeleteProject = (id) => {
    setCv((prevState) => {
      const newProjects = prevState.projects.filter(
        (projectItem) => projectItem.id !== id
      )
      return { ...prevState, projects: [...newProjects] }
    })
  }

  const handleLoadExample = () => {
    setCv(exampleCV)
  }

  const handleReset = () => {
    setCv(emptyCV)
  }

  const componentRef = useRef()

  // throws warning because react-to-print uses findDOMNode
  const validateCV = () => {
    if (
      !cv.personalInfo.firstName ||
      !cv.personalInfo.lastName ||
      !cv.personalInfo.title
    ) {
      alert("Please fill in the essential personal information fields.");
      return false;
    }
    return true;
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: validateCV,
  });

  return (
    <MainWrapper>
      <CVForm
        cv={cv}
        onChangePersonal={handleChangePersonal}
        onChangeExperience={handleChangeExperience}
        onAddExperience={handleAddExperience}
        onDeleteExperience={handleDeleteExperience}
        onChangeEducation={handleChangeEducation}
        onAddEducation={handleAddEducation}
        onDeleteEducation={handleDeleteEducation}
        onChangeProject={handleChangeProject}
        onAddProject={handleAddProject}
        onDeleteProject={handleDeleteProject}
        onPrint={handlePrint}
        onLoadExample={handleLoadExample}
        onReset={handleReset}
      />
      <CVPreviewWrapper>
        <CVPreview cv={cv} ref={componentRef} />
      </CVPreviewWrapper>
    </MainWrapper>
  )
}

export default Main

const MainWrapper = styled.main`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4rem;
  max-width: 1800px;
  padding: 4rem 8rem;
  margin: 0 auto;
  margin-bottom: 4rem;

  @media (max-width: 1600px) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`

const CVPreviewWrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
  padding: 1rem;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`
